import {
  AutoCompleteV2, Form, FormAutoCompleteV2Props, FormField,
  ICON_MAGNIFY,
} from '@doveit/bricks';
import React from 'react';
import { searchUsers } from '../../../providers/api/auth/authProvider';
import { CustomUser, UserRole } from '../../../types';
import { userRoleLabels } from '../../../labels';

const defaultPrintSuggestion = (user: CustomUser | CustomUser[]) => {
  if (Array.isArray(user)) return '';

  const rolesLabel = user['https://backoffice.dove.it/roles']
    ?.map((role) => userRoleLabels[role])
    .join(', ');

  return `${user.name} - ${rolesLabel}`;
};

export interface UserAutoCompleteProps extends Omit<FormAutoCompleteV2Props<CustomUser>, 'name' | 'suggestions' | 'printSuggestion' >, React.AriaAttributes {
  name?: string,
  roles?: UserRole[],
  printSuggestion?: (user: CustomUser | CustomUser[]) => string,
}

const UserAutoComplete: React.FC<UserAutoCompleteProps> = ({
  name,
  label,
  required,
  roles,
  error,
  warning,
  onSuggestionSelected,
  printLabelSuggestion,
  printSuggestion = defaultPrintSuggestion,
  ...rest
}) => {
  const fetchSuggestions = React.useCallback(async (search: string) => {
    const q = search.trim();

    return searchUsers({ q, roles });
  }, [roles]);

  const commonProps = React.useMemo(() => ({
    'aria-label': 'Seleziona utente',
    icon: ICON_MAGNIFY,
    suggestions: fetchSuggestions,
    printSuggestion,
    printLabelSuggestion,
    onSuggestionSelected,
  }), [fetchSuggestions, onSuggestionSelected, printSuggestion, printLabelSuggestion]);

  return name ? (
    <Form.AutoCompleteV2
      {...rest}
      {...commonProps}
      warning={warning}
      required={required}
      name={name}
      label={label}
    />
  ) : (
    <FormField
      label={label}
      required={required}
      error={error}
    >
      <AutoCompleteV2
        {...rest}
        {...commonProps}
        error={!!error}
      />
    </FormField>
  );
};

export default UserAutoComplete;
